import {
  clearPreviewBackendChoice,
  PREVIEW_API_PARAM,
  setPreviewBackendChoice,
} from '@savgroup-front-common/configuration/api/index';
import {
  DEV_BACKEND_NAME,
  getIsValidPreviewBackendName,
  getPreviewSlug,
} from '@savgroup-front-common/configuration/preview/previewBackend';
import { currentAppEnvironment } from '@savgroup-front-common/constants/shared/envs';
import { LOCAL_STORAGE_KEYS } from '@savgroup-front-common/types/LocalStorage';

import { PREVIEW_BAR_POSITION_KEY } from './PreviewBar.constants';

const RECENT_BACKEND_NAMES_KEY = 'rio_preview_recent';
const RECENT_BACKEND_NAMES_LIMIT = 5;
const OIDC_STORAGE_KEY_PREFIX = 'oidc.';
const BACKEND_HEALTH_PATH = '/ReverseAuthorizationService/health';
const HTTP_OK_STATUS = 200;
const PULL_REQUEST_URL_PREFIX =
  'https://github.com/revers-io/reversio-front/pull/';
const SELECTED_SELLER_KEY = `${currentAppEnvironment}_${LOCAL_STORAGE_KEYS.SELECTED_SELLER}`;
const PREVIEW_BAR_HIDDEN_COOKIE = 'rio_preview_bar_hidden=true';
const COMBOBOX_POPUP_SELECTOR = '[data-slot="combobox-content"]';

export const MISSING_VALUE = '-';

// the combobox popup is portaled outside the panel: picking an option must not count as a click outside
export function keepPanelOpenOnComboboxPopup(event: Event): void {
  if (
    event.target instanceof Element &&
    event.target.closest(COMBOBOX_POPUP_SELECTOR)
  ) {
    event.preventDefault();
  }
}

// a session cookie rather than the sessionStorage, which only lasts for one tab
export function hidePreviewBarForSession(): void {
  document.cookie = `${PREVIEW_BAR_HIDDEN_COOKIE}; Path=/; Secure; SameSite=Lax`;
}

export const getIsPreviewBarHidden = (): boolean =>
  document.cookie.split('; ').includes(PREVIEW_BAR_HIDDEN_COOKIE);

export interface BuildInfo {
  version: string;
  previewSlug?: string;
  pullRequestNumber?: string;
  pullRequestUrl?: string;
}

// the deployment fills these placeholders after the build: a plain truthiness check would be folded by the bundler
const getDeployedValue = (value?: string): string | undefined =>
  [undefined, ''].includes(value) ? undefined : value;

export function getBuildInfo(): BuildInfo {
  const pullRequestNumber = getDeployedValue(
    import.meta.env.VITE_PREVIEW_PR_NUMBER,
  );

  return {
    version: import.meta.env.VITE_BUILD_NUMBER || MISSING_VALUE,
    previewSlug: getDeployedValue(import.meta.env.VITE_PREVIEW_SLUG),
    pullRequestNumber,
    pullRequestUrl:
      pullRequestNumber && `${PULL_REQUEST_URL_PREFIX}${pullRequestNumber}`,
  };
}

// control remembers it through react-use's useLocalStorage, which stores JSON
export function getSelectedSellerId(): string {
  try {
    const sellerId: unknown = JSON.parse(
      localStorage.getItem(SELECTED_SELLER_KEY) ?? 'null',
    );

    return typeof sellerId === 'string' && sellerId ? sellerId : MISSING_VALUE;
  } catch {
    return MISSING_VALUE;
  }
}

export const BACKEND_HEALTH = {
  CHECKING: 'CHECKING',
  UP: 'UP',
  DOWN: 'DOWN',
} as const;

export type BackendHealth =
  (typeof BACKEND_HEALTH)[keyof typeof BACKEND_HEALTH];

export async function fetchBackendHealth({
  backendUrl,
  signal,
}: {
  backendUrl: string;
  signal: AbortSignal;
}): Promise<BackendHealth> {
  try {
    const response = await fetch(new URL(BACKEND_HEALTH_PATH, backendUrl), {
      credentials: 'omit',
      signal,
    });

    return response.status === HTTP_OK_STATUS
      ? BACKEND_HEALTH.UP
      : BACKEND_HEALTH.DOWN;
  } catch {
    return BACKEND_HEALTH.DOWN;
  }
}

function getRecentBackendNames(): string[] {
  try {
    const recentBackendNames: unknown = JSON.parse(
      localStorage.getItem(RECENT_BACKEND_NAMES_KEY) ?? '[]',
    );

    return Array.isArray(recentBackendNames)
      ? recentBackendNames.filter(getIsValidPreviewBackendName)
      : [];
  } catch {
    return [];
  }
}

export const getBackendNameOptions = (typedBackendName: string): string[] => [
  ...new Set(
    [
      DEV_BACKEND_NAME,
      getPreviewSlug(window.location.hostname),
      ...getRecentBackendNames(),
      typedBackendName,
    ].filter(getIsValidPreviewBackendName),
  ),
];

function rememberRecentBackendName(name: string): void {
  const recentBackendNames = [
    name,
    ...getRecentBackendNames().filter((recentName) => recentName !== name),
  ].slice(0, RECENT_BACKEND_NAMES_LIMIT);

  localStorage.setItem(
    RECENT_BACKEND_NAMES_KEY,
    JSON.stringify(recentBackendNames),
  );
}

export interface PreviewBarPosition {
  left: number;
  top: number;
}

interface ClampPreviewBarPositionArgs {
  position: PreviewBarPosition;
  buttonSize: { width: number; height: number };
}

export const clampPreviewBarPosition = ({
  position,
  buttonSize,
}: ClampPreviewBarPositionArgs): PreviewBarPosition => ({
  left: Math.max(
    0,
    Math.min(position.left, window.innerWidth - buttonSize.width),
  ),
  top: Math.max(
    0,
    Math.min(position.top, window.innerHeight - buttonSize.height),
  ),
});

const getIsFiniteNumber = (value: unknown): value is number =>
  Number.isFinite(value);

export function getRememberedPreviewBarPosition():
  | PreviewBarPosition
  | undefined {
  try {
    const { left, top }: Partial<Record<keyof PreviewBarPosition, unknown>> =
      JSON.parse(localStorage.getItem(PREVIEW_BAR_POSITION_KEY) ?? '{}') ?? {};

    return getIsFiniteNumber(left) && getIsFiniteNumber(top)
      ? { left, top }
      : undefined;
  } catch {
    return undefined;
  }
}

export const rememberPreviewBarPosition = (
  position: PreviewBarPosition,
): void =>
  localStorage.setItem(PREVIEW_BAR_POSITION_KEY, JSON.stringify(position));

const clearStorageButPreviewBarSettings = (storage: Storage): void =>
  Object.keys(storage)
    .filter(
      (key) =>
        key !== RECENT_BACKEND_NAMES_KEY &&
        key !== PREVIEW_BAR_POSITION_KEY &&
        !key.startsWith(OIDC_STORAGE_KEY_PREFIX),
    )
    .forEach((key) => storage.removeItem(key));

// a reload keeping ?api= would apply it again over the backend just chosen
function reloadWithoutPreviewApiParam(): void {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);

  if (!params.has(PREVIEW_API_PARAM)) {
    window.location.reload();

    return;
  }

  params.delete(PREVIEW_API_PARAM);

  const cleanedSearch = params.toString();

  window.location.replace(
    `${pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${hash}`,
  );
}

export function reloadWithClearedStorage(): void {
  [localStorage, sessionStorage].forEach(clearStorageButPreviewBarSettings);
  reloadWithoutPreviewApiParam();
}

export function switchPreviewBackend(name: string): void {
  setPreviewBackendChoice(name);
  rememberRecentBackendName(name);
  reloadWithClearedStorage();
}

export function resetPreviewBackend(): void {
  clearPreviewBackendChoice();
  reloadWithClearedStorage();
}
